import {
  Component,
  computed,
  inject,
  input,
  InputSignal,
  linkedSignal,
  output,
  OutputEmitterRef,
  Signal,
} from '@angular/core';
import { IconComponent } from '@kinomap-dev/ng-ui-lib/basics';
import { TranslatePipe } from '@ngx-translate/core';
import { ColorService } from '../../services/color.service';

export const PAGINATOR_ELLIPSIS: 'ellipsis' = 'ellipsis';
export type PaginatorPage = number | typeof PAGINATOR_ELLIPSIS;

export const PAGINATOR_ITEMS_PER_PAGE_OPTIONS: number[] = [5, 10, 15, 25, 50];

const FIRST_PAGE: number = 1;
const MAX_PAGES_WITHOUT_ELLIPSIS: number = 5;

const range = (start: number, end: number): number[] =>
  Array.from({ length: Math.max(end - start + 1, 0) }, (_, index) => start + index);

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

@Component({
  selector: 'app-paginator',
  imports: [IconComponent, TranslatePipe],
  templateUrl: './paginator.html',
  styleUrl: './paginator.scss',
})
export class Paginator {
  readonly colorService: ColorService = inject(ColorService);

  isLoading: InputSignal<boolean> = input.required<boolean>();

  totalItems: InputSignal<number> = input.required<number>();
  page: InputSignal<number> = input.required<number>();
  itemsPerPage: InputSignal<number> = input.required<number>();
  lastPage: InputSignal<number> = input.required<number>();

  firstItemInCurrentPage: InputSignal<number> = input.required<number>();
  lastItemInCurrentPage: InputSignal<number> = input.required<number>();

  displayedTotalItems: Signal<number> = linkedSignal<{ isLoading: boolean; value: number }, number>(
    {
      source: () => ({ isLoading: this.isLoading(), value: this.totalItems() }),
      computation: (current, previous) =>
        current.isLoading && previous ? previous.value : current.value,
    },
  );

  displayedLastPage: Signal<number> = computed(() =>
    this.isLoading()
      ? Math.max(Math.ceil(this.displayedTotalItems() / this.itemsPerPage()), 1)
      : this.lastPage(),
  );
  displayedPage: Signal<number> = computed(() =>
    clamp(this.page(), FIRST_PAGE, Math.max(this.displayedLastPage(), FIRST_PAGE)),
  );
  displayedFirstItem: Signal<number> = computed(() => {
    if (!this.isLoading()) return this.firstItemInCurrentPage();
    if (this.displayedTotalItems() === 0) return 0;
    return (this.displayedPage() - 1) * this.itemsPerPage() + 1;
  });
  displayedLastItem: Signal<number> = computed(() =>
    this.isLoading()
      ? Math.min(this.displayedPage() * this.itemsPerPage(), this.displayedTotalItems())
      : this.lastItemInCurrentPage(),
  );

  isLastPage: Signal<boolean> = computed(() => this.displayedPage() === this.displayedLastPage());
  isFirstPage: Signal<boolean> = computed(() => this.displayedPage() === FIRST_PAGE);

  readonly ellipsis: typeof PAGINATOR_ELLIPSIS = PAGINATOR_ELLIPSIS;
  readonly itemsPerPageOptions: number[] = PAGINATOR_ITEMS_PER_PAGE_OPTIONS;

  allPages: Signal<number[]> = computed(() => range(FIRST_PAGE, this.displayedLastPage()));
  pages: Signal<PaginatorPage[]> = computed(() => {
    const lastPage: number = this.displayedLastPage();

    if (lastPage <= MAX_PAGES_WITHOUT_ELLIPSIS) return range(FIRST_PAGE, lastPage);

    const secondPage: number = FIRST_PAGE + 1;
    const beforeLastPage: number = lastPage - 1;
    const middlePage: number = clamp(this.displayedPage(), secondPage + 1, beforeLastPage - 1);

    return [
      FIRST_PAGE,
      middlePage > secondPage + 1 ? PAGINATOR_ELLIPSIS : secondPage,
      middlePage,
      middlePage < beforeLastPage - 1 ? PAGINATOR_ELLIPSIS : beforeLastPage,
      lastPage,
    ];
  });

  setItemPerPage: OutputEmitterRef<number> = output<number>();
  goToPage: OutputEmitterRef<number> = output<number>();
  nextPage: OutputEmitterRef<void> = output<void>();
  previousPage: OutputEmitterRef<void> = output<void>();

  goToSelectedPage(select: HTMLSelectElement): void {
    const selectedPage: number = +select.value;
    if (selectedPage && selectedPage !== this.displayedPage()) this.goToPage.emit(selectedPage);
  }
}
